if (!customElements.get("quick-view-modal")) {
  customElements.define(
    "quick-view-modal",
    class QuickViewModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickViewInfo-"]');
        if (!this.modalContent) return;
      }

      hide(preventFocus = false) {
        this.modalContent.innerHTML = "";
        if (this.buttonsEl) this.buttonsEl.remove();

        this.openedBy =
          this.openedBy
            ?.closest(".product-card")
            ?.querySelector(".product-card__name > a") || this.openedBy;
        if (preventFocus) this.openedBy = null;

        super.hide();

        document.body.dispatchEvent(new CustomEvent("quick-view-closed"));

        // styles for forced disable body scroll on iOS
        this.unlockScrollBodyOnIos();
      }

      show(opener) {
        if (!opener || !opener?.dataset?.productUrl) return;

        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        const btnSpinner = opener.querySelector(".button-loader");
        if (btnSpinner) btnSpinner.classList.remove("hidden");

        fetch(opener.dataset.productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              '[id^="MainProduct-"]'
            );
            if (!this.productElement) return;

            this.prepareProductHTML();
            this.setModalHTML(opener);
            this.initSlider();
            this.initShopifyElements();
            super.show(opener);
            this.setFocusElement(opener);

            // styles for forced disable body scroll on iOS
            this.lockScrollBodyOnIos();
          })
          .finally(() => {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
            if (btnSpinner) btnSpinner.classList.add("hidden");
          });
      }

      prepareProductHTML() {
        this.preventDuplicatedIDs();
        this.removeDOMElements(this.productElement);
      }

      removeDOMElements(mainProductElement) {
        const selectors = [
          ".product-popup",
          ".product__description",
          ".product__text",
          ".product__share",
          ".product-sticky-add-bar",
          ".sticky-product-nav",
          "product-modal",
          ".pickup-availability",
          ".availability-notification",
          "availability-notification-drawer",
          ".complementary-products",
          ".product__divider",
          ".product__media-toggle",
          ".product__media-mobile-icon",
          "[data-availability-notification-script]",
          "[data-hide-blocks-on-scroll-script]",
          ".product__gallery-blocks",
          ".product-featured-article",
          ".product-media-banner",
          ".custom-liquid",
          ".product-extra-option",
          ".product-accordion",
          ".product-app",
          ".product__richtext",
          ".product-form__checkout",
        ];
        const removingElements = mainProductElement.querySelectorAll(
          selectors.join(", ")
        );
        removingElements.forEach((el) => el.remove());

        const onlyOneBlockSelectors = [
          ".product-parameters",
          ".product__vendor",
          ".product__price",
          ".product__title",
          ".product__inventory",
          ".product__buy-buttons",
        ];

        onlyOneBlockSelectors.forEach((selector) => {
          const items = mainProductElement.querySelectorAll(selector);
          if (items.length > 1) {
            items.forEach((el, index) => {
              if (index > 0) el.remove();
            });
          }
        });
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickview-${sectionId}`
          );
        this.productElement
          .querySelectorAll("variant-selects, variant-radios")
          .forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
          });
      }

      setModalHTML(opener) {
        this.modalContent.innerHTML = this.productElement.innerHTML;

        this.reinjectScriptTags();
        this.createFullDetailsButton(opener);
        this.reorderElements();
        this.preventScrollBody();
        this.preventVariantURLSwitching();
        this.clearMediaClasses();
      }

      reinjectScriptTags() {
        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        this.modalContent.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPickerEl = this.modalContent.querySelector(
          "variant-radios,variant-selects"
        );
        if (variantPickerEl) {
          variantPickerEl.setAttribute("data-update-url", "false");
        }
      }

      preventScrollBody() {
        this.modalContent
          .querySelectorAll(".product-form__controls-group input[type=radio]")
          .forEach((input) => {
            input.addEventListener("focus", (e) => {
              const scrollTop =
                document.body.scrollTop || document.documentElement.scrollTop;
              setTimeout(() => {
                window.scrollTo(0, scrollTop);
              }, 0);
            });
          });
      }

      initShopifyElements() {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      initSlider() {
        const sliderEl = this.modalContent.querySelector(
          ".product__media-list"
        );
        if (!sliderEl) return;

        const sliderWrapper = sliderEl.querySelector(
          ".product__media-list-container"
        );
        const slides = sliderEl.querySelectorAll(".product__media-item");
        const buttonPrev = sliderEl.querySelector(".slider-nav-button--prev");
        const buttonNext = sliderEl.querySelector(".slider-nav-button--next");
        const paginationEl = sliderEl.querySelector(".slider-pagination");
        if (!sliderWrapper || slides.length < 2) {
          if (buttonPrev) buttonPrev.remove();
          if (buttonNext) buttonNext.remove();
          if (paginationEl) paginationEl.remove();
          return;
        }

        const galleryType = sliderEl.dataset?.desktopType || "slider_previews";

        if (galleryType !== "slider" && galleryType !== "slider_previews") {
          return;
        }

        let thumbsSlider = null;

        const thumbsEl = this.modalContent.querySelector(
          ".product__media-sublist"
        );

        if (galleryType === "slider_previews" && thumbsEl) {
          thumbsSlider = new Swiper(thumbsEl, {
            spaceBetween: 10,
            slidesPerView: 3,
            loop: false,
            direction: "horizontal",
            allowTouchMove: true,
            watchSlidesProgress: true,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            autoHeight: false,
            slideToClickedSlide: true,
            on: {
              touchEnd: function (swiper) {
                let range = 5;
                let diff = (swiper.touches.diff = swiper.isHorizontal()
                  ? swiper.touches.currentX - swiper.touches.startX
                  : swiper.touches.currentY - swiper.touches.startY);
                if (diff < range || diff > -range) swiper.allowClick = true;
              },
            },
            breakpoints: {
              576: {
                slidesPerView: 3,
                direction: "vertical",
              },
            },
          });
        }

        sliderEl.classList.add("swiper");
        sliderWrapper.classList.add("swiper-wrapper");
        slides.forEach((slide) => slide.classList.add("swiper-slide"));

        const isLoop = true;

        new Swiper(sliderEl, {
          slidesPerView: 1,
          spaceBetween: 10,
          autoHeight: false,
          loop: isLoop,
          direction: "horizontal",
          speed: 800,
          allowTouchMove: true,
          watchSlideProgress: true,
          mousewheel: {
            forceToAxis: true,
          },
          navigation: {
            nextEl: buttonNext,
            prevEl: buttonPrev,
            disabledClass: "swiper-button-disabled",
          },
          pagination: {
            el: paginationEl,
            clickable: true,
          },
          thumbs: {
            swiper: thumbsSlider ? thumbsSlider : "",
            multipleActiveThumbs: false,
            slideThumbActiveClass: "swiper-slide-thumb-active",
          },
          on: {
            slideChange: function () {
              window.pauseAllMedia(sliderEl);
              this.params.noSwiping = false;
            },
            slideChangeTransitionEnd: function () {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              const model3D =
                this.slides[activeIndex]?.querySelector("model-viewer");
              const posterBtn3D = this.slides[activeIndex]?.querySelector(
                ".shopify-model-viewer-ui__button--poster"
              );
              if (model3D && posterBtn3D) {
                posterBtn3D.removeAttribute("hidden");
              }
            },
            touchStart: function () {
              const activeIndex = isLoop ? this.realIndex : this.activeIndex;
              const model3D =
                this.slides[activeIndex]?.querySelector("model-viewer");
              if (model3D) {
                if (
                  !model3D.classList.contains(
                    "shopify-model-viewer-ui__disabled"
                  )
                ) {
                  this.params.noSwiping = true;
                  this.params.noSwipingClass = "swiper-slide";
                } else {
                  this.params.noSwiping = false;
                }
              }
            },
          },
        });
      }

      createFullDetailsButton(opener) {
        const buttonsWrapper = this.modalContent.querySelector(
          ".product-form__buttons-row"
        );
        if (!buttonsWrapper || !opener?.dataset?.productUrl) return;

        const moreBtn = document.createElement("a");
        moreBtn.innerHTML = `
          <span class="button__content">
            <span class="button__label-wrapper">
              <span class="button__label">${theme.quickviewMore}</span>
              <span class="button__label" aria-hidden="true">${theme.quickviewMore}</span>
            </span>
          </span>
        `;
        moreBtn.setAttribute("href", opener.dataset.productUrl);
        moreBtn.setAttribute(
          "class",
          "product__full-details button button--secondary focus-inset"
        );

        buttonsWrapper.appendChild(moreBtn);
      }

      reorderElements() {
        const inventoryEl = this.modalContent.querySelector(
          ".product__inventory"
        );
        this.buttonsEl = this.modalContent.querySelector(
          ".product__buy-buttons"
        );
        const mainContent = this.querySelector(".quick-view-modal__content");

        // moving buttons to the bottom of modal by theme design
        if (this.buttonsEl && mainContent) {
          mainContent.append(this.buttonsEl);
        }

        // moving inventory block closer to the buttons by theme design
        if (inventoryEl && this.buttonsEl) {
          this.buttonsEl.insertAdjacentElement("afterbegin", inventoryEl);
        }
      }

      clearMediaClasses() {
        const mediaWrapper = this.modalContent.querySelector(".product__media");
        if (!mediaWrapper) return;

        const keepClasses = ["product__media", "product__media--no-media"];
        const filteredClasses = [...mediaWrapper.classList].filter((cssClass) =>
          keepClasses.includes(cssClass)
        );

        mediaWrapper.className = filteredClasses.join(" ");
      }

      setFocusElement(opener) {
        const productCard = opener.closest("product-card");
        const productCardLink = productCard?.querySelector(
          ".product-card__link-overlay"
        );
        if (productCardLink) this.openedBy = productCardLink;
      }

      handleKeyDown(event) {
        this._tabPressed = event.key === "Tab";
      }

      handleFocusIn(event) {
        if (!this._tabPressed) return;
        this._tabPressed = false;

        this.ensureVisibleInsideModal(event.target);
      }

      ensureVisibleInsideModal(target) {
        if (this.contains(target) && target.closest(".product-parameters")) {
          const scrollableContainer = this.querySelector(
            ".quick-view-modal__content-wrapper"
          );
          const modalRect = scrollableContainer.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();

          if (targetRect.bottom > modalRect.bottom - this.buttonsGroupHeight) {
            scrollableContainer.scrollTop +=
              targetRect.bottom -
              modalRect.bottom +
              this.buttonsGroupHeight +
              24;
          }
        }
      }

      lockScrollBodyOnIos() {
        if (navigator.userAgent.indexOf("iPhone") > -1) {
          this.openScrollY = window.scrollY;
          document.documentElement.style.setProperty(
            "--current-body-scroll",
            `${-1 * this.openScrollY}px`
          );
          document.documentElement.style.setProperty(
            "scroll-behavior",
            "unset"
          );
          document.body.classList.add("forced-scroll-locked");
        }
      }

      unlockScrollBodyOnIos() {
        if (navigator.userAgent.indexOf("iPhone") > -1) {
          document.body.classList.remove("forced-scroll-locked");
          document.documentElement.scrollTop = this.openScrollY;
          document.documentElement.style.removeProperty(
            "--current-body-scroll"
          );
          document.documentElement.style.removeProperty("scroll-behavior");
        }
      }
    }
  );
}
